import React from 'react';
import { Box } from '@mui/material';
import { Peak } from './types';
import { createTimeline } from './utils';

interface PresetPreviewChartProps {
  peaks: Peak[];
  timeLabels: string[];
  width?: number;
  height?: number;
  color?: string;
  selected?: boolean;
}

const PresetPreviewChart: React.FC<PresetPreviewChartProps> = ({
  peaks,
  timeLabels,
  width = 70,
  height = 35,
  color = '#ff9800',
  selected = false
}) => {
  // Generate the timeline data
  const data = peaks.length > 0 ? createTimeline(timeLabels, peaks) : Array(timeLabels.length).fill(1);
  
  // Normalize data to fit in the chart height
  const maxValue = Math.max(...data);
  const normalizedData = data.map(value => (value / maxValue) * (height - 4));
  
  // Create SVG path
  const barWidth = width / data.length;
  const bars = data.map((value, index) => {
    const barHeight = normalizedData[index];
    const x = index * barWidth;
    const y = height - barHeight;
    
    return (
      <rect
        key={index}
        x={x}
        y={y}
        width={barWidth - 1}
        height={barHeight}
        fill={color}
        opacity={selected ? 0.9 : 0.8}
        stroke={selected ? '#f57c00' : '#ff9800'}
        strokeWidth={selected ? 0.5 : 0.3}
      />
    );
  });

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: width,
        height: height,
        border: selected ? '2px solid #ff9800' : '1px solid #e0e0e0',
        borderRadius: 1,
        backgroundColor: selected ? '#fff3e0' : '#fafafa',
        transition: 'all 0.2s ease-in-out'
      }}
    >
      <svg width={width} height={height} style={{ display: 'block' }}>
        {/* Background grid for better visibility */}
        <defs>
          <pattern id="grid" width={barWidth} height={height} patternUnits="userSpaceOnUse">
            <path d={`M ${barWidth} 0 L 0 0 0 ${height}`} fill="none" stroke="#e0e0e0" strokeWidth="0.5"/>
          </pattern>
        </defs>
        <rect width={width} height={height} fill="url(#grid)" />
        {bars}
      </svg>
    </Box>
  );
};

export default PresetPreviewChart; 